import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { MetasService } from '../services/metas.service';
import { SchemasService } from '../services/schemas.service';
import { UsedEquipmentService } from '../services/used-equipment.service';
import { EquipmentListSubcategory } from '../used-equipments/models/equipmentList.model';
import { Metas } from '../services/models/metas.model';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { BannerComponent } from './components/banner/banner.component';
import { NewCarouselComponent } from './components/new-carousel/new-carousel.component';
import { UsedCarouselComponent } from './components/used-carousel/used-carousel.component';
import { TechnologiesComponent } from './components/technologies/technologies.component';
import { CardListComponent } from '../used-equipments/components/card-list/card-list.component';
import { AboutComponent } from './components/about/about.component';

@Component({
  selector: 'app-home',
  imports: [
    CommonModule,
    RouterModule,
    SlickCarouselModule,
    AboutComponent,
    BannerComponent,
    NewCarouselComponent,
    UsedCarouselComponent,
    TechnologiesComponent,
    CardListComponent,
  ],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home {
  private metasService = inject(MetasService);
  private schemasService = inject(SchemasService);
  private usedEquipmentService = inject(UsedEquipmentService);
  private platformId = inject(PLATFORM_ID);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  tabOption = signal(0); // 0: News, 1: Used
  carouselAction = signal<string>('');
  relatedEquipments = signal<EquipmentListSubcategory[]>([]);
  countEquipmentCurrent = signal(0);
  limitEquipments = signal(3);

  equipmentsConfig = {
    slidesToShow: 4,
    slidesToScroll: 4,
    infinite: true,
    arrows: false,
    lazyLoad: 'ondemand',
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: false,
          centerMode: true,
          variableWidth: true,
        },
      },
    ],
  };

  constructor() {
    this.elementRef.nativeElement.removeAttribute('ng-version');

    if (typeof window !== 'undefined') {
      this.updateShowButtons();
    }

    this.addMetas();
    this.addSchema();
    this.getUsedEquipmentsHome();

    this.destroyRef.onDestroy(() => {
      this.schemasService.removeLdJsonScript();
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.updateShowButtons();
    this.updateNavColor();
  }

  @HostListener('window:scroll')
  onScroll() {
    this.updateNavColor();
  }

  private updateNavColor() {
    if (typeof document === 'undefined') return;

    const nav = document.querySelector('nav');
    if (!nav) return;

    if (window.innerWidth < 994 || window.scrollY > 46) {
      nav.classList.remove('removeColorsNav');
    } else {
      nav.classList.add('removeColorsNav');
    }
  }

  private addMetas() {
    const metas: Metas = {
      description:
        'Distribuidor autorizado de soluciones industriales para venta y renta de maquinaria pesada.',
      image: 'https://s3.amazonaws.com/tracsa.com/opengraph-tracsa/HOME.jpg',
      robots: 'index,follow',
      subject: 'Soluciones industriales de maquinaria pesada',
      title: 'Maquinaria pesada, agrícola, industrial y equipo ligero | Tracsa Cat®',
      schema: {},
    };
    this.metasService.addMetas(metas);
  }

  private addSchema() {
    if (!isPlatformBrowser(this.platformId)) {
      const json = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Tracsa Cat',
        alternateName: 'Tracsa Caterpillar',
        url: 'https://tracsa.com.mx/',
        logo: 'https://tracsa.com.mx/assets/general/logo.png',
        image: 'https://tracsa.com.mx/assets/general/logo.png',
        description:
          'Tracsa Cat es el distribuidor autorizado de Caterpillar en el centro-occidente de México desde 1974. Ofrece venta, renta y servicio de maquinaria pesada y ligera, refacciones originales, soluciones de energía y equipos agrícolas. Opera en 9 estados con sede en Tlaquepaque, Jalisco, atendiendo los sectores de construcción, minería y agricultura.',
        foundingDate: '1974',
        slogan: 'Maquinaria Caterpillar para la construcción, minería y agricultura',
        brand: {
          '@type': 'Brand',
          name: 'Caterpillar',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+52-800-561-4944',
          contactType: 'customer service',
          areaServed: [
            'Jalisco',
            'Guanajuato',
            'Zacatecas',
            'Michoacán',
            'Colima',
            'Querétaro',
            'Nayarit',
            'Aguascalientes',
            'San Luis Potosí',
          ],
          availableLanguage: 'Spanish',
        },
        location: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Periférico Sur 7800, Santa María Tequepexpan',
            addressLocality: 'San Pedro Tlaquepaque',
            addressRegion: 'Jalisco',
            postalCode: '45601',
            addressCountry: 'MX',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: '20.61099478748533',
            longitude: '-103.3993242592407',
          },
        },
        areaServed: {
          '@type': 'State',
          name: [
            'Jalisco',
            'Guanajuato',
            'Zacatecas',
            'Michoacán',
            'Colima',
            'Querétaro',
            'Nayarit',
            'Aguascalientes',
            'San Luis Potosí',
          ],
        },
        sameAs: [
          'https://www.facebook.com/TracsaCATMatriz/',
          'https://www.instagram.com/tracsacat/',
          'https://www.youtube.com/user/TracsaOficial',
          'https://www.linkedin.com/company/tracsacat',
          'https://x.com/TracsaCat',
        ],
        additionalType: 'LocalBusiness',
        subOrganization: [
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Aguascalientes',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'León-Aguascalientes 121, Col. Arellanos',
              addressLocality: 'Aguascalientes',
              addressRegion: 'Aguascalientes',
              postalCode: '20340',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '21.8853',
              longitude: '-102.2916',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Colima',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Carretera Colima Jiquilpan 1075, Col. Juana de Asbaje',
              addressLocality: 'Colima',
              addressRegion: 'Colima',
              postalCode: '28085',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '19.2433',
              longitude: '-103.7247',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Puerto Vallarta',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Carretera Tepic-Puerto Vallarta 1150 Sur Col. Nuevo Vallarta',
              addressLocality: 'Nuevo Vallarta',
              addressRegion: 'Nayarit',
              postalCode: '63732',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '20.6984',
              longitude: '-105.2697',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat San Luis Potosí',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Carretera 57 No. 6801, Col. Villa de Pozos',
              addressLocality: 'San Luis Potosí',
              addressRegion: 'San Luis Potosí',
              postalCode: '78394',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '22.1565',
              longitude: '-100.9855',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat León',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Blvd. Aeropuerto 2335, Col. Ejido los López',
              addressLocality: 'León',
              addressRegion: 'Guanajuato',
              postalCode: '37680',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '21.1619',
              longitude: '-101.6821',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Querétaro',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Km 8 +800 m, México 120 Col. Industrial Balvanera',
              addressLocality: 'El Pueblito',
              addressRegion: 'Querétaro',
              postalCode: '76908',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '20.5888',
              longitude: '-100.4417',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Guadalajara',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Periférico Sur 7800 Col. Santa María Tequepexpan',
              addressLocality: 'Tlaquepaque',
              addressRegion: 'Jalisco',
              postalCode: '45601',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '20.61099478748533',
              longitude: '-103.3993242592407',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Tepic',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Blvd. Luis Donaldo Colosio 19-B, Col. Ciudad Industrial',
              addressLocality: 'Tepic',
              addressRegion: 'Nayarit',
              postalCode: '63175',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '21.5041',
              longitude: '-104.8942',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Zacatecas',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Carretera Fresnillo 54 KM 9 + 028 Col. La Pimienta',
              addressLocality: 'Zacatecas',
              addressRegion: 'Zacatecas',
              postalCode: '98053',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '22.7709',
              longitude: '-102.5832',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Morelia',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Carretera Salamanca - Morelia 850, Col. San José Trinidad',
              addressLocality: 'Morelia',
              addressRegion: 'Michoacán',
              postalCode: '58880',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '19.7060',
              longitude: '-101.1949',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Lázaro Cárdenas',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Zona Industrial Lote 51-A, Col. Zona Industrial Fideicomiso',
              addressLocality: 'Lázaro Cárdenas',
              addressRegion: 'Michoacán',
              postalCode: '60950',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '17.9583',
              longitude: '-102.1961',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Uruapan',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Libramiento Oriente 9095, Col. Zumpimito',
              addressLocality: 'Uruapan',
              addressRegion: 'Michoacán',
              postalCode: '60190',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '19.4204',
              longitude: '-102.0631',
            },
          },
          {
            '@type': 'LocalBusiness',
            name: 'Tracsa Cat Manzanillo',
            telephone: '+52-800-561-4944',
            priceRange: '$$$$',
            address: {
              '@type': 'PostalAddress',
              streetAddress: 'Carretera Federal 200, El libramiento, Km 95 + 21 Col. Jalipa',
              addressLocality: 'Manzanillo',
              addressRegion: 'Colima',
              postalCode: '28870',
              addressCountry: 'MX',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '19.0543',
              longitude: '-104.2897',
            },
          },
        ],
      };
      this.schemasService.addLdJsonScript(json);
    }
  }

  private getUsedEquipmentsHome() {
    this.usedEquipmentService.getUsedEquipmentsHome().subscribe({
      next: (result) => this.relatedEquipments.set(result.reverse()),
      error: console.error,
    });
  }

  changeTabOption(option: number) {
    this.tabOption.set(option);
  }

  clickPrev() {
    this.carouselAction.set('prev');
  }

  clickNext() {
    this.carouselAction.set('next');
  }

  getCountEquipment(count: any) {
    this.countEquipmentCurrent.set(count);
  }

  private updateShowButtons() {
    this.limitEquipments.set(window.innerWidth <= 992 ? 1 : 3);
  }
}
